import { useState } from "react";
import { Layers, Search, Copy, Archive, ArchiveRestore, ChevronDown, ChevronRight, Check, X, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";

const IMPORT_AS_BADGE = {
  "Internal Field": "bg-emerald-50 text-emerald-700",
  "Dynamic Form Answer": "bg-sky/60 text-navy",
  "Ignore Column": "bg-slate-100 text-slate-500",
};

function TemplateRow({ tpl, onChanged }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tpl.template_name);
  const [busy, setBusy] = useState(false);

  const act = async (fn) => { setBusy(true); try { await fn(); onChanged(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };

  const rename = () => act(async () => {
    await api.put(`/import-templates/${tpl.id}`, { ...tpl, template_name: name.trim() || tpl.template_name });
    toast.success("Template renamed"); setEditing(false);
  });
  const duplicate = () => act(async () => { await api.post(`/import-templates/${tpl.id}/duplicate`); toast.success("Template duplicated"); });
  const toggleArchive = () => act(async () => {
    const next = tpl.status === "Active" ? "Archived" : "Active";
    await api.post(`/import-templates/${tpl.id}/status`, { status: next });
    toast.success(next === "Archived" ? "Template archived" : "Template activated");
  });

  return (
    <div data-testid={`template-row-${tpl.id}`} className="rounded-xl border border-sky/60 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 p-3">
        <div className="flex min-w-0 items-center gap-2">
          <button onClick={() => setOpen((o) => !o)} className="text-ink/40 hover:text-navy">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
          {editing ? (
            <div className="flex items-center gap-1">
              <input value={name} onChange={(e) => setName(e.target.value)} className="h-8 rounded-lg border border-sky px-2 text-sm" data-testid={`template-rename-input-${tpl.id}`} />
              <button onClick={rename} disabled={busy} className="rounded p-1 text-emerald-600 hover:bg-emerald-50"><Check className="h-4 w-4" /></button>
              <button onClick={() => { setEditing(false); setName(tpl.template_name); }} className="rounded p-1 text-ink/40 hover:bg-mist"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-navy">
                {tpl.template_name}
                <button onClick={() => setEditing(true)} data-testid={`template-rename-${tpl.id}`} className="text-ink/30 hover:text-loop"><Pencil className="h-3 w-3" /></button>
              </p>
              <p className="truncate text-[11px] text-ink/45">{tpl.source_type}{tpl.source_label ? ` · ${tpl.source_label}` : ""} · {tpl.column_mappings?.length || 0} cols · updated {fmtDateTime(tpl.updated_at)}</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tpl.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{tpl.status}</span>
          <button onClick={duplicate} disabled={busy} data-testid={`template-duplicate-${tpl.id}`} title="Duplicate" className="rounded-lg border border-sky p-1.5 text-ink/60 hover:bg-mist"><Copy className="h-3.5 w-3.5" /></button>
          <button onClick={toggleArchive} disabled={busy} data-testid={`template-archive-${tpl.id}`} title={tpl.status === "Active" ? "Archive" : "Activate"} className="rounded-lg border border-sky p-1.5 text-ink/60 hover:bg-mist">
            {tpl.status === "Active" ? <Archive className="h-3.5 w-3.5" /> : <ArchiveRestore className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-sky/50 p-3" data-testid={`template-columns-${tpl.id}`}>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {(tpl.column_mappings || []).map((cm, i) => (
              <div key={i} className="flex items-center justify-between gap-2 rounded-lg bg-mist/40 px-2.5 py-1.5 text-xs">
                <span className="truncate font-medium text-navy">{cm.source_column}</span>
                <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${IMPORT_AS_BADGE[cm.import_as] || "bg-mist"}`}>
                  {cm.import_as === "Internal Field" ? cm.internal_field : cm.import_as === "Dynamic Form Answer" ? `Form: ${cm.question_label || cm.source_column}` : "Ignored"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MappingTemplatesPanel({ templates, onChanged }) {
  const [q, setQ] = useState("");
  const filtered = templates.filter((t) => t.template_name.toLowerCase().includes(q.toLowerCase()) || (t.source_label || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid="mapping-templates-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-sora font-semibold text-navy"><Layers className="h-5 w-5 text-loop" />Mapping Templates</h2>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-ink/40" />
          <input data-testid="template-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…" className="h-9 w-56 rounded-lg border border-sky pl-8 pr-2 text-sm outline-none focus:border-loop" />
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink/40">No templates yet. Save one from the import review screen to reuse it for recurring layouts.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => <TemplateRow key={t.id} tpl={t} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );
}
